// React Query hooks for the global categories collection.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  setCategoryActive,
  hardDeleteCategory,
  type CreateCategoryInput,
  type UpdateCategoryInput,
} from '@/lib/api/categories';

const KEYS = {
  all: ['categories'] as const,
  list: (filters?: { isActive?: boolean }) =>
    [...KEYS.all, 'list', filters ?? {}] as const,
};

export const categoryKeys = KEYS;

/** List categories (sorted by sortOrder asc, then name asc). */
export const useCategories = (filters?: { isActive?: boolean }) =>
  useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => listCategories(filters),
  });

export const useCreateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) => createCategory(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useUpdateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      updateCategory(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/** Soft-delete (set isActive=false). */
export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useSetCategoryActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setCategoryActive(id, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useHardDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      productCount,
    }: {
      id: string;
      productCount: number;
    }) => hardDeleteCategory(id, productCount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};
